import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import MetadataService from '@services/metadata'

import { catchAxiosError } from '@utils/utils'
import { Languages, LocaleType } from '@const/consts'

import { IMetadataState } from './types'

const LocaleLabels: Record<string, Record<LocaleType, string>> = {
  [Languages.EN]: {
    [LocaleType.EN_GB]: 'English (UK)',
    [LocaleType.EN]: 'English (US)',
    [LocaleType.RU]: 'Russian',
    [LocaleType.PT]: 'Portuguese',
    [LocaleType.VI]: 'Vietnamese'
  },
  [Languages.PT]: {
    [LocaleType.EN_GB]: 'Ingles (Reino Unido)',
    [LocaleType.EN]: 'Ingles (Estados Unidos)',
    [LocaleType.RU]: 'Russo',
    [LocaleType.PT]: 'Portugues',
    [LocaleType.VI]: 'Vietnamita'
  },
  [Languages.RU]: {
    [LocaleType.EN_GB]: 'Английский (Великобритания)',
    [LocaleType.EN]: 'Английский (США)',
    [LocaleType.RU]: 'Русский',
    [LocaleType.PT]: 'Португальский',
    [LocaleType.VI]: 'Вьетнамский'
  },
  [Languages.VI]: {
    [LocaleType.EN_GB]: 'Tieng Anh (Vuong quoc Anh)',
    [LocaleType.EN]: 'Tieng Anh (Hoa Ky)',
    [LocaleType.RU]: 'Tieng Nga',
    [LocaleType.PT]: 'Tieng Bo Dao Nha',
    [LocaleType.VI]: 'Tieng Viet'
  }
}

const getLanguageCode = (acceptLanguage?: string): string => {
  const language = acceptLanguage?.replace('_', '-').split('-')[0].toLowerCase()

  return language === 'vn' ? Languages.VI : language || Languages.EN
}

const getHardcodedLocales = (acceptLanguage?: string): Record<string, string> => (
  LocaleLabels[getLanguageCode(acceptLanguage)] ?? LocaleLabels[Languages.EN]
)

const initialState: IMetadataState = {
  locales: getHardcodedLocales(),
  timeZones: {}
}

export const getLocales = createAsyncThunk(
  'metadata/getLocales',
  async (acceptLanguage: string | undefined, { getState }) => {
    const { language } = (getState() as { utils: { language: string } }).utils

    return getHardcodedLocales(acceptLanguage ?? language)
  }
)

export const getTimeZones = createAsyncThunk(
  'metadata/getTimeZones',
  async (_, { rejectWithValue }) => {
    try {
      const response = await MetadataService.getTimeZones()

      return response.data
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

export const metadataSlice = createSlice({
  initialState,
  name: 'metadata',
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLocales.fulfilled, (state, action) => {
        if (!action.payload) return

        state.locales = action.payload
      })
      .addCase(getTimeZones.fulfilled, (state, action) => {
        if (!action.payload) return

        state.timeZones = action.payload
      })
  }
})

const { reducer } = metadataSlice

export default reducer
